import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Download, Loader2, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = {
  standard: "Standard Lease",
  pro: "Professional Lease",
  unlimited: "Unlimited Covenant",
};

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = res.data;

      if (data.payment_status === "paid" && data.download_token) {
        // Download endpoint requires the buyer's email as proof — we stashed
        // it in sessionStorage from CartDrawer before the Stripe round-trip.
        const stashedEmail =
          (typeof window !== "undefined" &&
            window.sessionStorage.getItem("ss_checkout_email")) || "";
        const dlRes = await axios.get(
          `${API}/downloads/catalog/${data.download_token}`,
          { params: stashedEmail ? { email: stashedEmail } : {} }
        );
        setOrder(dlRes.data);
        setStatus("success");
        // Stash done its job — clear so the email doesn't linger in
        // sessionStorage across tabs/refreshes.
        try {
          window.sessionStorage.removeItem("ss_checkout_email");
        } catch (_) {
          /* ignore */
        }
        return;
      }

      if (data.payment_status === "paid" && !data.download_token) {
        // Payment confirmed but order still processing
        if (attempts < 10) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus("processing");
        }
        return;
      }

      if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Still pending
      if (attempts < 10) {
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } else {
        setStatus("timeout");
      }
    } catch (err) {
      console.error("Status poll error:", err);
      if (attempts < 10) {
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="max-w-2xl w-full" data-testid="success-page">
        {status === "loading" && (
          <div className="text-center" data-testid="success-loading">
            <Loader2 size={48} className="animate-spin text-gold mx-auto mb-6" />
            <h1
              className="text-3xl font-medium text-[#EAEAEA] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Processing Your Order...
            </h1>
            <p className="text-[#737373]">Confirming your payment with Stripe.</p>
          </div>
        )}

        {status === "success" && order && (
          <div data-testid="success-order">
            <div className="text-center mb-10">
              <CheckCircle size={48} className="text-gold mx-auto mb-6" />
              <h1
                className="text-4xl font-medium text-[#EAEAEA] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Your Beats Are Ready
              </h1>
              <p className="text-[#737373]">
                Order {order.order_id} — Each download link is short-lived;
                refresh this page to re-issue.
              </p>
            </div>

            <div className="space-y-4">
              {Object.entries(order.items || {}).map(([beatId, item]) => (
                <div
                  key={beatId}
                  data-testid={`download-item-${beatId}`}
                  className="bg-[#0F0F0F] border border-[#1A1A1A] p-5 rounded-sm"
                >
                  <h3 className="text-lg text-[#EAEAEA] font-medium mb-1">
                    {item.beat_title}
                  </h3>
                  <p className="text-xs text-gold uppercase tracking-wider mb-4">
                    {TIER_LABELS[item.tier] || item.tier}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(item.links || {}).map(([fileType, url]) => (
                      <a
                        key={fileType}
                        href={url.startsWith("http") ? url : `${BACKEND_URL}${url}`}
                        data-testid={`download-${fileType}-${beatId}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 text-gold text-sm font-medium uppercase tracking-wider rounded-sm hover:bg-gold hover:text-black transition-all"
                      >
                        <Download size={14} />
                        {fileType.replace("_", " ")}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/"
                data-testid="back-to-store"
                className="text-sm text-[#737373] hover:text-gold transition-colors"
              >
                Back to Store
              </Link>
            </div>
          </div>
        )}

        {(status === "error" || status === "timeout" || status === "expired") && (
          <div className="text-center" data-testid="success-error">
            <XCircle size={48} className="text-red-400 mx-auto mb-6" />
            <h1
              className="text-3xl font-medium text-[#EAEAEA] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="text-[#737373] mb-6">
              {status === "expired"
                ? "Your payment session has expired. Please try again."
                : "We couldn't confirm your payment. If you were charged, contact info@sanctumsonics.com."}
            </p>
            <Link
              to="/"
              className="inline-block px-8 py-3 bg-gold text-black font-medium text-sm tracking-widest uppercase rounded-sm"
            >
              Return to Store
            </Link>
          </div>
        )}

        {status === "processing" && (
          <div className="text-center" data-testid="success-processing">
            <Loader2 size={48} className="animate-spin text-gold mx-auto mb-6" />
            <h1
              className="text-3xl font-medium text-[#EAEAEA] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Payment Confirmed
            </h1>
            <p className="text-[#737373]">
              Your order is being prepared. You'll receive a download link via email shortly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
